import React from 'react';
import PropTypes from 'prop-types';
import { Card, MOET_CONSTANTS } from 'moet-core';

import { Typography, SpacerVertical } from '@klarna/bubble-ui';

import { generateCyElemId } from 'moet-test-utils/cypress/utils';
import { CY_BANK_ACCOUNT_CONTAINER_IDS } from '@/containers/BankAccounts/constants';
import { LinkWithoutHref } from '@/components';
import { Column, Row } from 'mage-components';
import { Message, translator } from 'mage-i18n';

import { compose } from 'recompose';
import { connect } from 'react-redux';
import { getFeatureByKey } from '@/selectors/featuresConfig';
import { FEATURES } from '@/constants';

const { COUNTRIES_WITH_LOCAL_BANK_ACCOUNT } = MOET_CONSTANTS;

const calculateTabIndexOffsets = (cardIndex, rhythm) => {
  const offset = cardIndex * rhythm;
  const tail = offset + rhythm;
  return [offset, tail];
};

const TAB_INDEX_PADDING = 1;
const tContainerPrefix = 'products-fe.containers.bank_account';

const BankAccountStack = ({
  canAdd,
  children,
  merchantCountry,
  onCardAdd,
  onCardRemove,
  t,
  tabIndexRhythm,
  isDisabled,
  supportLocalUKBankAccounts,
}) => {
  const lastTabIndexEnd = React.Children.count(children) * tabIndexRhythm;
  const isRemovable = index => index > 0;

  return (
    <>
      {React.Children.map(children, (child, index) => {
        const [tabIndexStart, tabIndexAfter] = calculateTabIndexOffsets(
          index,
          tabIndexRhythm + TAB_INDEX_PADDING,
        );
        return (
          <React.Fragment key={`card-fragment-for-${child.key}`}>
            <Card hasFooterLink>
              <SpacerVertical spaceToken="space/150" />
              {React.cloneElement(child, { tabIndexStart })}
              {isRemovable(index) && (
                <Row>
                  <Column>
                    <div
                      style={{
                        flex: 0,
                        display: 'flex',
                        justifyContent: 'flex-end',
                      }}
                    >
                      <LinkWithoutHref
                        textToken="text-style/text/labels/body/medium"
                        onPress={e => {
                          e.preventDefault();
                          onCardRemove(child.key);
                        }}
                        disabled={isDisabled}
                        UNSAFE_props={{ tabIndex: tabIndexAfter }}
                      >
                        {t(`${tContainerPrefix}.buttons.remove`)}
                      </LinkWithoutHref>
                    </div>
                  </Column>
                </Row>
              )}
            </Card>
            <SpacerVertical spaceToken="space/150" />
          </React.Fragment>
        );
      })}
      {/* we collect only one local bank account */}

      {(supportLocalUKBankAccounts && merchantCountry === 'GB') ||
      (merchantCountry !== 'GB' && COUNTRIES_WITH_LOCAL_BANK_ACCOUNT.includes(merchantCountry)) ? (
        <SpacerVertical spaceToken="space/150" />
      ) : canAdd ? (
        <LinkWithoutHref
          textToken="text-style/text/labels/body/medium"
          onPress={e => {
            e.preventDefault();
            onCardAdd();
          }}
          UNSAFE_props={{
            ...generateCyElemId(CY_BANK_ACCOUNT_CONTAINER_IDS.BTN_ADD_BANK_ACCOUNT, window),
            tabIndex: lastTabIndexEnd,
          }}
        >
          {t(`${tContainerPrefix}.buttons.add_bank_account`)}
        </LinkWithoutHref>
      ) : (
        <Typography textToken="text-style/text/super-paragraphs/body/regular">
          <Message path={`${tContainerPrefix}.no_more_bank_accounts`} />
          <SpacerVertical spaceToken="space/150" />
        </Typography>
      )}
    </>
  );
};

BankAccountStack.propTypes = {
  canAdd: PropTypes.bool.isRequired,
  isDisabled: PropTypes.bool.isRequired,
  merchantCountry: PropTypes.string.isRequired,
  t: PropTypes.func.isRequired,
  tabIndexRhythm: PropTypes.number.isRequired,
  onCardAdd: PropTypes.func.isRequired,
  onCardRemove: PropTypes.func.isRequired,
  children: PropTypes.node,
  supportLocalUKBankAccounts: PropTypes.func,
};

export default compose(
  connect(state => ({
    t: translator(state),
    supportLocalUKBankAccounts: getFeatureByKey(FEATURES.SUPPORT_LOCAL_UK_BANK_ACCOUNTS)(state),
  })),
)(BankAccountStack);
